import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, TrendingUp, Users, Brain, Zap, ThumbsUp, ThumbsDown, X, Mail, MessageSquare } from 'lucide-react';
import './FAQ.css';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());
  const [feedbackPopup, setFeedbackPopup] = useState({ visible: false, faqIndex: null, type: null });
  const [feedbackForm, setFeedbackForm] = useState({ email: '', comment: '', subscribe: false });
  const [submissionStatus, setSubmissionStatus] = useState({ loading: false, success: false, error: null });
  const popupRef = useRef(null);

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
      // Close feedback popup if FAQ is closing
      if (feedbackPopup.faqIndex === index) {
        setFeedbackPopup({ visible: false, faqIndex: null, type: null });
      }
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const showFeedbackPopup = (faqIndex, type) => {
    setFeedbackPopup({ visible: true, faqIndex, type });
    setFeedbackForm({ email: '', comment: '', subscribe: false });
    setSubmissionStatus({ loading: false, success: false, error: null });
  };

  const closeFeedbackPopup = () => {
    setFeedbackPopup({ visible: false, faqIndex: null, type: null });
    setSubmissionStatus({ loading: false, success: false, error: null });
  };

  const handleFormChange = (field, value) => {
    setFeedbackForm(prev => ({ ...prev, [field]: value }));
  };

  const submitFeedback = async () => {
    if (!feedbackForm.email && feedbackPopup.type === 'dislike') {
      setSubmissionStatus({ loading: false, success: false, error: 'Email is required for feedback' });
      return;
    }

    setSubmissionStatus({ loading: true, success: false, error: null });

    try {
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? '/api/faq-feedback'  // ✅ Your server URL
      : 'http://localhost:3001/api/faq-feedback';       // ✅ Local backend URL

        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          faqIndex: feedbackPopup.faqIndex,
          faqQuestion: faqData[feedbackPopup.faqIndex].question,
          feedbackType: feedbackPopup.type,
          userEmail: feedbackForm.email,
          userComment: feedbackForm.comment,
          subscribeToAnalytics: feedbackForm.subscribe
        }),
      });

      if (response.ok) {
        setSubmissionStatus({ loading: false, success: true, error: null });
        setTimeout(() => {
          closeFeedbackPopup();
        }, 2000);
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      setSubmissionStatus({ loading: false, success: false, error: error.message });
    }
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closeFeedbackPopup();
      }
    };

    if (feedbackPopup.visible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [feedbackPopup.visible]);

  const faqData = [
    {
      question: "What is TPHILE token and how can I earn it?",
      answer: "TPHILE is our native cryptocurrency token deployed on the Polygon blockchain with a limited supply of 1 billion tokens. You can earn TPHILE by submitting high-quality predictions (base 500 TPHILE with quality multipliers), referring friends (10,000 TPHILE per verified referral, max 10), engaging on social media, and actively participating in our community. Your earnings depend on your prediction quality score (70-100 range) and activity bonuses.",
      icon: <Brain className="faq-question-icon" />
    },
    {
      question: "When will TPHILE tokens be distributed?",
      answer: "Token distribution begins at our Token Generation Event (TGE), which triggers when we reach 10,000 verified users AND 10,000 total predictions. We're targeting launch within 6-8 months. At TGE, you'll receive 50% of your earned tokens immediately, with the remaining 50% unlocking gradually over 6 months through our vesting schedule.",
      icon: <Zap className="faq-question-icon" />
    },
    {
      question: "What can I do with my TPHILE tokens?",
      answer: "TPHILE tokens unlock premium experiences within our ecosystem: obtain exclusive platform badges that showcase your expertise, access bespoke data analysis tailored to your specific needs, request personalized consultation services with our intelligence team, and enjoy priority access to new features. Your tokens create a thriving marketplace where insight generation meets tangible value.",
      icon: <TrendingUp className="faq-question-icon" />
    },
    {
      question: "Why is there a vesting schedule for TPHILE tokens?",
      answer: "Our vesting schedule protects you and the entire community. Instead of distributing 100% of tokens at launch (which could cause dramatic price volatility), we allocate 50% immediately at TGE with the remaining 50% unlocking monthly over 6 months. This ensures market stability, rewards long-term commitment, and prevents potential price crashes from mass selling. Your tokens remain secure in smart contracts, automatically unlocking each month.",
      icon: <Users className="faq-question-icon" />
    },
    {
      question: "How does the referral program work?",
      answer: "Earn 10,000 TPHILE tokens for each friend you refer (maximum 10 referrals). Your friend must verify their email and make at least 3 predictions for the referral to be valid. This helps us build a quality community while rewarding you for expanding our network with engaged users.",
      icon: <Users className="faq-question-icon" />
    },
    {
      question: "What is the total supply and budget for TPHILE?",
      answer: "TPHILE has a fixed total supply of 1 billion tokens, ensuring scarcity and value. Our initial budget allocation is $300 for the token generation and distribution infrastructure. The limited supply combined with our quality-focused reward system creates sustainable token economics designed for long-term value appreciation.",
      icon: <Brain className="faq-question-icon" />
    },
    {
      question: "How is my prediction quality score calculated?",
      answer: "Your quality score (70-100 range) is determined by our multi-layered AI analysis using OpenAI models combined with rule-based algorithms. We assess prediction clarity, supporting evidence, logical reasoning, historical accuracy, and uniqueness. Higher scores multiply your base reward of 500 TPHILE, and activity bonuses can increase your earnings by up to 100%. Building a strong track record increases both your reliability score and token earnings.",
      icon: <TrendingUp className="faq-question-icon" />
    },
    {
      question: "What blockchain is TPHILE built on and why?",
      answer: "TPHILE is deployed on the Polygon blockchain, chosen for its low transaction fees, fast confirmation times, and environmental sustainability. Polygon's scalability ensures seamless token transactions and transfers without the high gas fees associated with Ethereum mainnet, making it cost-effective for our community to use their earned tokens.",
      icon: <Zap className="faq-question-icon" />
    },
    {
      question: "What is the best AI for predicting market trends?",
      answer: "Trendphile doesn't directly predict market trends. Instead, we use advanced machine learning, deep learning, and neural networks to analyze high-quality predictions from users with proven reliability scores. Our AI processes collective intelligence from our community to generate valuable insights and identify emerging trend patterns.",
      icon: <Brain className="faq-question-icon" />
    },
    {
      question: "How accurate are AI predictions on Trendphile?",
      answer: "Trendphile's accuracy comes from analyzing predictions made by users with high reliability scores. Our AI doesn't make predictions itself, but rather processes and validates community predictions using machine learning algorithms. We achieve high insight accuracy by focusing on users with demonstrated forecasting capabilities and applying rigorous quality filters.",
      icon: <TrendingUp className="faq-question-icon" />
    },
    {
      question: "Can AI really predict the future?",
      answer: "Trendphile doesn't claim to predict the future. Instead, our AI analyzes high-quality predictions from reliable community members to identify patterns, trends, and consensus insights. We believe the collective intelligence of knowledgeable users, when properly analyzed by AI, provides more valuable insights than any single prediction system.",
      icon: <Zap className="faq-question-icon" />
    },
    {
      question: "What makes Trendphile different from other prediction platforms?",
      answer: "Trendphile stands out through our community-driven approach combined with AI-powered analysis. We don't generate predictions ourselves - we analyze and validate predictions from users with proven track records. Our platform focuses on collective intelligence, transparency, and providing insights based on the wisdom of reliable community members.",
      icon: <Users className="faq-question-icon" />
    },
    {
      question: "How does trend prediction work on your platform?",
      answer: "Trendphile offers enterprise-grade API integration and application services for comprehensive data stream analysis and real-time monitoring. Our platform provides professional online analytics, continuously processing your proprietary data streams, business metrics, and market indicators enhanced by our community's validated predictions with high reliability scores. The system delivers instant insights, trend detection, and predictive intelligence through customizable dashboards with real-time updates and advanced monitoring capabilities for your organization.",
      icon: <TrendingUp className="faq-question-icon" />
    },
    {
      question: "Is Trendphile suitable for professional traders and analysts?",
      answer: "Absolutely! Trendphile provides advanced analytics and insights derived from our community's most reliable predictors. Professional traders and analysts use our platform to access collective intelligence, monitor trend consensus, and gain insights from users with proven forecasting abilities. Our AI-powered analysis helps professionals make more informed decisions.",
      icon: <Brain className="faq-question-icon" />
    },
    {
      question: "What types of predictions can I make on Trendphile?",
      answer: "You can create predictions across various categories including technology trends, market forecasts, social movements, economic indicators, consumer behavior, and more. Our platform supports both quantitative and qualitative prediction formats. Your predictions contribute to our collective intelligence pool and may be analyzed by our AI if you maintain a high reliability score.",
      icon: <Zap className="faq-question-icon" />
    },
    {
      question: "How do I get started with making predictions?",
      answer: "Simply create your free account, explore existing predictions to understand our quality standards, then use our intuitive prediction builder to create well-researched forecasts. Focus on providing supporting evidence and thoughtful analysis - this helps build your reliability score and contributes to the platform's collective intelligence.",
      icon: <Users className="faq-question-icon" />
    },
    {
      question: "Can I track the performance of my predictions over time?",
      answer: "Yes! Trendphile provides comprehensive performance tracking with detailed analytics, reliability scoring, historical performance charts, and insights into prediction outcomes. Building a strong track record increases your reliability score and makes your predictions more likely to be included in our AI analysis for generating platform insights.",
      icon: <TrendingUp className="faq-question-icon" />
    },
    {
      question: "Is there a mobile app for Trendphile?",
      answer: "Currently, Trendphile is optimized as a responsive web application that works seamlessly on mobile devices. We're developing a dedicated mobile app with enhanced real-time monitoring, data stream integration, and advanced analytics features that will be available soon for both iOS and Android platforms.",
      icon: <Zap className="faq-question-icon" />
    }
  ];

  return (
    <section className="faq-section">
      <div className="faq-container">
        <div className="faq-header">
          <h2 className="faq-title">Frequently Asked Questions</h2>
          <p className="faq-subtitle">
            Everything you need to know about Trendphile's AI-powered analysis platform and community-driven insights.
          </p>
        </div>

        <div className="faq-list" role="list">
          {faqData.map((item, index) => (
            <div 
              key={index}
              className={`faq-item ${openItems.has(index) ? 'active' : ''}`}
              role="listitem"
            >
              <button
                className="faq-question"
                onClick={() => toggleItem(index)}
                aria-expanded={openItems.has(index)}
                aria-controls={`faq-answer-${index}`}
                id={`faq-question-${index}`}
              >
                <div className="faq-question-content">
                  {item.icon}
                  <span className="faq-question-text">{item.question}</span>
                </div>
                <ChevronDown className="faq-icon" aria-hidden="true" />
              </button>
              
              <div 
                className="faq-answer"
                id={`faq-answer-${index}`}
                aria-labelledby={`faq-question-${index}`}
                role="region"
              >
                <div className="faq-answer-content">
                  {item.answer}
                  
                  {/* Feedback Buttons */}
                  {openItems.has(index) && (
                    <div className="faq-feedback-buttons">
                      <p className="feedback-prompt">Was this answer helpful?</p>
                      <div className="feedback-actions">
                        <button 
                          className="feedback-btn feedback-btn-like"
                          onClick={() => showFeedbackPopup(index, 'like')}
                        >
                          <ThumbsUp size={16} />
                          <span>Yes</span>
                        </button>
                        <button 
                          className="feedback-btn feedback-btn-dislike"
                          onClick={() => showFeedbackPopup(index, 'dislike')}
                        >
                          <ThumbsDown size={16} />
                          <span>No</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback Popup */}
      {feedbackPopup.visible && (
        <div className="feedback-popup-overlay">
          <div className="feedback-popup" ref={popupRef}>
            <div className="feedback-popup-header">
              <h3>
                {feedbackPopup.type === 'like' ? (
                  <>
                    <ThumbsUp size={20} />
                    Thank you for your feedback!
                  </>
                ) : (
                  <>
                    <ThumbsDown size={20} />
                    Help us improve
                  </>
                )}
              </h3>
              <button className="feedback-popup-close" onClick={closeFeedbackPopup}>
                <X size={20} />
              </button>
            </div>

            <div className="feedback-popup-content">
              {submissionStatus.success ? (
                <div className="feedback-success">
                  <p>✅ Thank you! Your feedback has been submitted.</p>
                  {feedbackForm.subscribe && (
                    <p>📊 You'll receive weekly analytics updates.</p>
                  )}
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); submitFeedback(); }}>
                  <div className="feedback-field">
                    <label htmlFor="feedback-email">
                      <Mail size={16} />
                      Email {feedbackPopup.type === 'dislike' ? '*' : '(optional)'}
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      placeholder="your@email.com"
                      value={feedbackForm.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      required={feedbackPopup.type === 'dislike'}
                    />
                  </div>

                  {feedbackPopup.type === 'dislike' && (
                    <div className="feedback-field">
                      <label htmlFor="feedback-comment">
                        <MessageSquare size={16} />
                        How can we improve this answer?
                      </label>
                      <textarea
                        id="feedback-comment"
                        placeholder="Tell us what information was missing or unclear..."
                        value={feedbackForm.comment}
                        onChange={(e) => handleFormChange('comment', e.target.value)}
                        rows={3}
                      />
                    </div>
                  )}

                  <div className="feedback-checkbox">
                    <input
                      id="subscribe-analytics"
                      type="checkbox"
                      checked={feedbackForm.subscribe}
                      onChange={(e) => handleFormChange('subscribe', e.target.checked)}
                    />
                    <label htmlFor="subscribe-analytics">
                      📊 Send me weekly analytics and insights
                    </label>
                  </div>

                  {submissionStatus.error && (
                    <div className="feedback-error">
                      ❌ {submissionStatus.error}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="feedback-submit"
                    disabled={submissionStatus.loading}
                  >
                    {submissionStatus.loading ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        })}
      </script>
    </section>
  );
};

export default FAQ;
